import { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { employeeService } from '../../services/employee.service';
import { EmployeeMap } from '../../components/maps/EmployeeMap';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { LoadingSkeleton } from '../../components/common/LoadingSkeleton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { tokens } from '../../theme';
import type { Employee } from '../../types';

const REFRESH_INTERVAL = 10000;

type EmployeeMarkerStatus = 'active' | 'idle' | 'interrupted' | 'offline';

function getStatus(employee: Employee): EmployeeMarkerStatus {
  if (employee.lastLocation) {
    const minutesAgo = (Date.now() - new Date(employee.lastLocation.timestamp).getTime()) / 60000;
    if (minutesAgo < 5) return 'active';
    if (minutesAgo < 30) return 'idle';
    return 'interrupted';
  }
  if (employee.onDuty) return 'active';
  return 'offline';
}

const STATUS_META: Record<EmployeeMarkerStatus, { label: string; color: string; bg: string }> = {
  active:      { label: 'Active',      color: tokens.status.success.text, bg: tokens.status.success.bg },
  idle:        { label: 'Idle',        color: tokens.status.warning.text, bg: tokens.status.warning.bg },
  interrupted: { label: 'Interrupted', color: tokens.status.danger.text,  bg: tokens.status.danger.bg },
  offline:     { label: 'Off Duty',    color: tokens.status.neutral.text, bg: tokens.status.neutral.bg },
};

function EmployeeListPanel({
  employees,
  search,
  onSearch,
  selectedId,
  onSelect,
}: {
  employees: Employee[];
  search: string;
  onSearch: (v: string) => void;
  selectedId?: string;
  onSelect: (e: Employee) => void;
}) {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ p: 2, pb: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search employees..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search employees' }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {employees.length === 0 ? (
          <EmptyState
            title={search ? 'No matches' : 'No employees on map'}
            description={
              search
                ? 'Try a different name or employee code.'
                : 'Employees appear here when they share live location.'
            }
            icon={<PeopleAltRoundedIcon sx={{ fontSize: 32, color: 'neutral.400' }} />}
          />
        ) : (
          <List disablePadding>
            {employees.map((employee, i) => {
              const status = getStatus(employee);
              const meta = STATUS_META[status];
              const isSelected = selectedId === employee.id;
              return (
                <Box key={employee.id}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelect(employee)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      bgcolor: isSelected ? (t) => alpha(t.palette.primary.main, 0.06) : 'transparent',
                      borderLeft: '3px solid',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: meta.bg,
                          color: meta.color,
                          fontSize: '0.8rem',
                          fontWeight: 700,
                        }}
                      >
                        {employee.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {employee.name}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25} flexWrap="wrap">
                          <FiberManualRecordRoundedIcon sx={{ fontSize: 8, color: meta.color }} />
                          <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600 }}>
                            {meta.label}
                          </Typography>
                          {employee.lastLocation?.batteryLevel !== undefined && (
                            <>
                              <Typography variant="caption" color="text.secondary">·</Typography>
                              <BatteryChargingFullRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {employee.lastLocation.batteryLevel}%
                              </Typography>
                            </>
                          )}
                        </Stack>
                      }
                    />
                    <Chip
                      label={employee.todayVisitsCount ?? 0}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', minWidth: 28, fontWeight: 700 }}
                      title="Today's visits"
                    />
                  </ListItemButton>
                  {i < employees.length - 1 && <Divider sx={{ ml: 8 }} />}
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

function EmployeeDetailCard({
  employee,
  onClose,
}: {
  employee: Employee;
  onClose: () => void;
}) {
  const status = getStatus(employee);
  const meta = STATUS_META[status];

  return (
    <Card sx={{ mt: { xs: 0, md: 2 } }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
              }}
            >
              {employee.name.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {employee.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {employee.employeeCode} · {employee.department ?? 'Field'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close employee details"
              sx={{ ml: 'auto', display: { sm: 'none' } }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 2, sm: 3 }}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {[
              { label: 'Status', value: meta.label, color: meta.color },
              {
                label: 'Last update',
                value: employee.lastLocation
                  ? formatDistanceToNow(new Date(employee.lastLocation.timestamp), { addSuffix: true })
                  : 'Never',
              },
              { label: "Today's visits", value: String(employee.todayVisitsCount ?? 0) },
              {
                label: 'Battery',
                value:
                  employee.lastLocation?.batteryLevel !== undefined
                    ? `${employee.lastLocation.batteryLevel}%`
                    : 'N/A',
              },
            ].map((item) => (
              <Box key={item.label} sx={{ minWidth: 72 }}>
                <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: item.color ?? 'text.primary' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>

          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close employee details"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function LiveMapPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const {
    data: employees,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['live-locations'],
    queryFn: () => employeeService.getLiveLocations(),
    refetchInterval: REFRESH_INTERVAL,
  });

  useEffect(() => {
    const interval = setInterval(() => setLastRefresh(new Date()), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    refetch();
    setLastRefresh(new Date());
  };

  const filteredEmployees = (employees ?? []).filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.employeeCode.toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) return <LoadingSkeleton variant="detail" />;
  if (error) {
    const apiMessage =
      (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      ?? (error as Error)?.message;
    return (
      <ErrorMessage
        title="Could not load live map"
        message={apiMessage || 'Failed to load employee locations. Please try again.'}
        onRetry={() => refetch()}
      />
    );
  }

  const activeCount = (employees ?? []).filter((e) => getStatus(e) === 'active').length;

  const listPanel = (
    <EmployeeListPanel
      employees={filteredEmployees}
      search={search}
      onSearch={setSearch}
      selectedId={selectedEmployee?.id}
      onSelect={(e) => {
        setSelectedEmployee(e);
        if (isMobile) setListOpen(false);
      }}
    />
  );

  return (
    <Box>
      <PageHeader
        compact
        title="Live Map"
        subtitle={`${activeCount} / ${employees?.length ?? 0} active · Auto-refresh every 30s`}
        secondaryAction={
          isMobile ? (
            <Chip
              icon={<PeopleAltRoundedIcon />}
              label="Employees"
              onClick={() => setListOpen(true)}
              clickable
              sx={{ fontWeight: 600 }}
            />
          ) : undefined
        }
        action={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Updated {formatDistanceToNow(lastRefresh, { addSuffix: true })}
            </Typography>
            <Tooltip title="Refresh now">
              <IconButton onClick={handleRefresh} size="small" aria-label="Refresh locations">
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          height: { xs: 'auto', md: 'calc(100vh - 220px)' },
          minHeight: { xs: 420, md: 520 },
        }}
      >
        {!isMobile && (
          <Card
            sx={{
              width: { md: 320, lg: 340 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {listPanel}
          </Card>
        )}

        <Card sx={{ flex: 1, minWidth: 0, overflow: 'hidden', minHeight: { xs: 360, md: 0 } }}>
          <Box sx={{ height: { xs: 360, md: '100%' }, position: 'relative' }}>
            <EmployeeMap
              employees={filteredEmployees}
              height="100%"
              onMarkerClick={setSelectedEmployee}
            />
          </Box>
        </Card>
      </Box>

      {selectedEmployee && (
        <EmployeeDetailCard employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} />
      )}

      <Drawer
        anchor="bottom"
        open={isMobile && listOpen}
        onClose={() => setListOpen(false)}
        PaperProps={{
          sx: {
            height: '70vh',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
          },
        }}
      >
        <Box sx={{ pt: 1.5, px: 2, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'neutral.300' }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
          Field employees
        </Typography>
        {listPanel}
      </Drawer>
    </Box>
  );
}
